import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import FabulaIcon from '../../components/FabulaIcon';

export default function KinderfeestjesPage() {
  useEffect(() => {
    document.title = 'Fabula familierestaurant kinderfeestje Schijndel';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Vier een kinderfeestje bij Fabula in Schijndel. Spelen in binnen en buitenspeeltuin en samen genieten van lekker eten. Vraag direct aan.');
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Kinderfeestjes bij Fabula Schijndel');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Een stressvrij kinderfeestje. Spelen, eten en volop plezier bij Fabula.');
    }

    const keywords = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      keywords.setAttribute('content', 'kinderfeestje schijndel, kinderfeestje brabant, kinderfeestje restaurant, fabula kinderfeestje, speeltuin kinderfeestje, feestje met midgetgolf, uitje kinderen schijndel, kinderfeestje arrangement, verjaardag kind vieren, familierestaurant kinderfeestje');
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const scriptId = 'schema-kinderfeestjes';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'EventVenue',
        name: 'Kinderfeestjes bij Fabula Schijndel',
        url: `${siteUrl}/kinderfeestjes`,
        description: 'Vier een kinderfeestje bij Fabula in Schijndel. Spelen in binnen en buitenspeeltuin en samen genieten van lekker eten.',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Boxtelseweg 24',
          addressLocality: 'Schijndel',
          postalCode: '5481 VG',
          addressCountry: 'NL',
        },
        telephone: '+31735495005',
        offers: [
          {
            '@type': 'Offer',
            name: 'Saffier & Diamant Pakket',
            price: '13.50',
            priceCurrency: 'EUR',
            description: 'Pannenkoek of friet, onbeperkt drinken, ijs toe, versierde feesttafel en onbeperkt spelen in de speeltuin.',
          },
        ],
      });
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  const extraActivities = [
    {
      title: 'Pannenkoek versieren',
      price: '€ 2,50 per kind extra',
      age: '4–12 jaar',
      icon: 'ri-cake-3-line',
      link: '/reserveren-kinderfeestje-pannenkoek-versieren',
    },
    {
      title: 'Midgetgolf',
      price: '€ 4,25 per kind extra',
      age: '4–12 jaar',
      icon: 'ri-flag-line',
      link: '/reserveren-kinderfeestje-midgetgolf',
    },
    {
      title: 'Escaperoom Ocean Adventures',
      price: '€ 5,95 per kind extra',
      age: '6–12 jaar',
      icon: 'ri-ship-line',
      link: '/reserveren-kinderfeestje-escaperoom-ocean',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-48 pb-28 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/de9fb16518d5448d93b469caa3268f04/e46d2fdec69828d8c73b13434fca5ba2.jpeg"
            alt="Kinderfeestje bij Fabula"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/20"></div>
        </div>

        <div className="absolute top-0 left-0 w-96 h-96 bg-[#feb912]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#feb912]/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-block bg-[#feb912]/20 backdrop-blur-sm px-6 py-2 rounded-full mb-6"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-[#feb912] font-semibold text-sm uppercase tracking-wider">Kinderfeestjes</span>
            </motion.div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Vier je feestje bij Fabula!
            </h1>
            
            <p className="text-xl text-white/80 max-w-3xl mx-auto mb-12 leading-relaxed">
              Een onvergetelijk kinderfeestje met heerlijk eten en eindeloos speelplezier. Wij zorgen voor alles, jullie genieten!
            </p>

            <Link
              to="/reserveren-kinderfeestje"
              className="inline-block bg-[#feb912] hover:bg-white text-[#3c3b3c] font-bold px-10 py-4 rounded-full transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer text-lg"
            >
              Boek jouw kinderfeestje
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Intro Section */}
      <section 
        className="py-28 relative overflow-hidden bg-white"
      >
        {/* Subtle Glow Effect */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-purple-400/15 rounded-full blur-[120px]"></div>
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-purple-400/15 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-400/15 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-purple-400/15 rounded-full blur-[120px]"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-yellow-300/15 rounded-full blur-[140px]"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-[#3c3b3c] mb-6">
              Zorgeloos feest vieren
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-12">
              Bij Fabula organiseren we het perfecte kinderfeestje. Terwijl de kinderen zich uitleven in onze grote speeltuin, kunnen ouders ontspannen genieten. Wij zorgen voor het eten, de drinken en een onvergetelijke dag!
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                className="bg-white rounded-2xl shadow-lg p-8 border-2 border-[#feb912]/20"
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mb-4 flex justify-center">
                  <FabulaIcon type="menukaart" color="yellow" size="lg" animate />
                </div>
                <h3 className="text-xl font-bold text-[#3c3b3c] mb-3">Heerlijk eten</h3>
                <p className="text-gray-600">
                  Compleet verzorgd met o.a. drinken, pannenkoeken of frietjes
                </p>
              </motion.div>

              <motion.div
                className="bg-white rounded-2xl shadow-lg p-8 border-2 border-[#feb912]/20"
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mb-4 flex justify-center">
                  <FabulaIcon icon="ri-gamepad-line" color="dark" size="lg" animate />
                </div>
                <h3 className="text-xl font-bold text-[#3c3b3c] mb-3">Speelplezier</h3>
                <p className="text-gray-600">
                  Onbeperkt spelen in onze grote binnen- en buitenspeeltuin
                </p>
              </motion.div>

              <motion.div
                className="bg-white rounded-2xl shadow-lg p-8 border-2 border-[#feb912]/20"
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mb-4 flex justify-center">
                  <FabulaIcon type="kinderfeest" color="yellow" size="lg" animate />
                </div>
                <h3 className="text-xl font-bold text-[#3c3b3c] mb-3">Zorgeloos</h3>
                <p className="text-gray-600">
                  Wij regelen alles, jullie hoeven alleen maar te genieten
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Package Section */}
      <section className="py-28 bg-gradient-to-br from-[#3c3b3c] via-[#4a4849] to-[#3c3b3c] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#feb912]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#feb912]/5 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Onze feestpakketten
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Kies uit onze compleet verzorgde pakketten
            </p>
          </motion.div>

          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border-2 border-[#feb912]/30 shadow-2xl">
              <div className="text-center mb-8">
                <div className="inline-block bg-[#feb912] px-6 py-2 rounded-full mb-4">
                  <span className="text-[#3c3b3c] font-bold text-sm uppercase tracking-wider">Populair</span>
                </div>
                <h3 className="text-4xl font-bold text-white mb-4">Saffier & Diamant Pakket</h3>
                <div className="text-5xl font-bold text-[#feb912] mb-2">€13,50</div>
                <p className="text-white/80">per kind</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white/5 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-white mb-4 flex items-center">
                    <i className="ri-check-line text-[#feb912] mr-2"></i>
                    Inbegrepen
                  </h4>
                  <ul className="space-y-3 text-white/90">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#feb912] mr-2 mt-1"></i>
                      <span>Pannenkoek of friet naar keuze</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#feb912] mr-2 mt-1"></i>
                      <span>Onbeperkt drinken (limonade &amp; water)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#feb912] mr-2 mt-1"></i>
                      <span>IJs toe als dessert</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#feb912] mr-2 mt-1"></i>
                      <span>Versierde feesttafel</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#feb912] mr-2 mt-1"></i>
                      <span>Onbeperkt spelen in de speeltuin</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white/5 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-white mb-4 flex items-center">
                    <i className="ri-information-line text-[#feb912] mr-2"></i>
                    Praktisch
                  </h4>
                  <ul className="space-y-3 text-white/90">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#feb912] mr-2 mt-1"></i>
                      <span>Minimaal 5 kinderen</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#feb912] mr-2 mt-1"></i>
                      <span>Duur: 3/5 uur</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#feb912] mr-2 mt-1"></i>
                      <span>Reserveren verplicht</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#feb912] mr-2 mt-1"></i>
                      <span>Ouders betalen reguliere menuprijs</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-[#feb912] mr-2 mt-1"></i>
                      <span>Het hele jaar door beschikbaar</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="text-center">
                <Link
                  to="/reserveren-kinderfeestje"
                  className="inline-block bg-[#feb912] hover:bg-white text-[#3c3b3c] font-bold px-10 py-4 rounded-full transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer text-lg"
                >
                  <i className="ri-calendar-check-line mr-2 text-xl"></i>
                  Boek dit pakket
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Extra Activities */}
      <section 
        className="py-28 relative overflow-hidden bg-white"
      >
        {/* Subtle Glow Effect */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-purple-400/15 rounded-full blur-[120px]"></div>
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-purple-400/15 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-400/15 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-purple-400/15 rounded-full blur-[120px]"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-yellow-300/15 rounded-full blur-[140px]"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-[#3c3b3c] mb-6">
              Extra activiteiten
            </h2>
            <p className="text-lg text-gray-600">
              Maak het feestje nóg specialer met deze extra's
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {extraActivities.map((activity, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="h-full"
              >
                <Link
                  to={activity.link}
                  className="group block bg-white rounded-2xl shadow-lg p-8 border-2 border-[#feb912]/20 hover:bg-[#3c3b3c] hover:border-[#3c3b3c] transition-all duration-300 cursor-pointer h-full flex flex-col"
                >
                  <div className="mb-6 flex justify-center flex-shrink-0">
                    <FabulaIcon
                      icon={activity.icon}
                      color={index % 2 === 0 ? 'yellow' : 'dark'}
                      size="lg"
                      animate
                    />
                  </div>
                  
                  <div className="flex-grow flex flex-col">
                    <div className="h-16 flex items-center justify-center mb-4">
                      <h3 className="text-xl font-bold text-[#3c3b3c] group-hover:text-white text-center transition-colors duration-300">
                        {activity.title}
                      </h3>
                    </div>
                    
                    <div className="text-center mt-auto">
                      <div className="text-lg font-bold text-[#feb912] group-hover:text-white mb-1 transition-colors duration-300">
                        {activity.price}
                      </div>
                      <div className="text-sm text-gray-600 group-hover:text-white/90 transition-colors duration-300">
                        {activity.age}
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <p className="text-gray-600 mb-6">
              Klik op een activiteit om direct te reserveren met deze extra
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 bg-gradient-to-br from-[#3c3b3c] via-[#4a4849] to-[#3c3b3c] relative overflow-hidden">
        <motion.div 
          className="absolute top-0 left-0 w-96 h-96 bg-[#feb912]/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        ></motion.div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight max-w-4xl mx-auto">
              Klaar voor een onvergetelijk feestje?
            </h2>
            
            <p className="text-2xl text-[#feb912] mb-12 font-medium">
              Boek nu en maak er een bijzondere dag van!
            </p>

            <Link
              to="/reserveren-kinderfeestje"
              className="inline-block bg-[#feb912] hover:bg-white text-[#3c3b3c] font-bold px-10 py-5 rounded-full transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-110 whitespace-nowrap cursor-pointer text-lg"
            >
              <span className="flex items-center">
                <i className="ri-gift-line mr-2 text-xl"></i>
                Boek jouw kinderfeestje
              </span>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}